<template>
    <div>
        <div class="label ml-2" @click="openDialog" :data-test="`${sourceName}-more-filter-btn`">
            <v-icon class="filter-icon">fal fa-filter ml-1</v-icon>
            More Filters
        </div>

        <fe-dialog
            title="More Filters"
            v-if="showDialog"
            @accept="apply"
            @close="close"
            persistent
            dismissButtonText="Cancel"
            acceptButtonText="Apply"
            width="600"
        >
            <div class="filter-content">
                <template v-for="(f, i) in defaultDemographics">
                    <div 
                        :key="f.model + '-wrapper-' + i" 
                        class="d-flex justify-space-between flex-row mb-3"
                    >
                        <fe-label class="filter-label">{{f.label}}</fe-label>
                        
                        <fe-remote-combo
                            v-if="f.model == 'ell_flag'"
                            v-model="defaultDemographicFilters[f.model]"
                            :itemValue="f.itemValue"
                            :itemText="f.itemText"
                            :items="f.items"
                            class="filter-component"
                            hide-details
                            single
                            chips
                            byId
                            :data-test="`${sourceName}-${f.model}-select`"
                        />
                        
                        <fe-remote-exclude-combo
                            v-else
                            v-model="defaultDemographicFilters[f.model]"
                            :itemValue="f.itemValue"
                            :itemText="f.itemText"
                            :items="f.items"
                            class="filter-component"
                            hide-details
                            @input="f.onChange ? f.onChange(f) : () => {}"
                            :data-test="`${sourceName}-${f.model}-select`"
                        />
                    </div>
                </template>
                
                <div class="custom-demographics-section">
                    <advanced-demographic-filters 
                        v-model="advancedDemographicParams" 
                        ref="filters" 
                        vertical
                        :resetEntry="false"
                    />
                </div>
            </div>
        </fe-dialog>
    </div>
</template>

<script>
import AdvancedDemographicFilters from "@/components/common/AdvancedDemographicFilters.vue";

export default {
    name: "MoreFilters",

    components: {AdvancedDemographicFilters},

    props: {
        value: { type: Object, default: () => {} },
        sourceName: { type: String, default: 'chronic-absence' }
    },

    data() {
        return {
            showDialog: false,
            defaultDemographicFilters: {
                disability_type_id: [],
                ell_flag: null,
                ell_level_id: [],
                meal_status_id: []
            },
            customDemographicFilters: {}
        }
    },

    computed: {
        advancedDemographicParams: {
            get() { 
                return this.customDemographicFilters || {}
            },
            set(obj) { 
                this.customDemographicFilters = obj 
            }
        },

        defaultDemographics() {
            const baseFields = [{
                label: 'Disability',
                model: 'disability_type_id',
                itemText: "description",
                itemValue: "id",
                items: [
                    {
                        id: -1,
                        description: 'Any Disability'
                    }, {
                        id: 0,
                        description: 'No Disabilities'
                    }
                ],
                onChange: v => this.handleSelectionConflicts(v)
            }, {
                label: 'EL Status', 
                itemText: 'name',
                itemValue: 'id',
                model: 'ell_flag',
                items: [{
                    id: '0',
                    name: 'No'
                }, {
                    id: '1',
                    name: 'Yes'
                }]
            }, {
                label: 'ELP Level',
                model: 'ell_level_id',
                itemText: 'display_name',
                itemValue: 'id',
                items: [
                    {
                        id: -1,
                        display_name: 'Any ELP Level'
                    }, {
                        id: 0,
                        display_name: 'No ELP Level'
                    }
                ],
                onChange: v => this.handleSelectionConflicts(v)
            }, {
                label: 'Meal Status',
                model: 'meal_status_id',
                itemText: 'name',
                itemValue: 'id',
                items: [
                    {
                        id: -1,
                        name: 'Any Meal Status'
                    }, {
                        id: 0,
                        name: 'No Meal Status'
                    }
                ],
                onChange: v => this.handleSelectionConflicts(v)
            }]

            // Filter out meal status if user doesn't have permissions
            return baseFields.filter(field => {
                if (field.model === 'meal_status_id') {
                    return this.$hasSecurity('SEARCH_MEAL_STATUS') || this.$hasSecurity('DRILLDOWN_MEAL_STATUS')
                }
                return true
            })
        }
    },

    watch: {
        value: {
            handler(v) {
                if (!v || Object.keys(v).length === 0) {
                    return
                }
                
                const defaultDemographicKeys = ['disability_type_id', 'ell_flag', 'ell_level_id', 'meal_status_id']
                
                // Extract default demographic filters - only set if key exists in v
                this.defaultDemographicFilters = {
                    disability_type_id: v && 'disability_type_id' in v ? v.disability_type_id : [],
                    ell_flag: v && 'ell_flag' in v ? v.ell_flag : null,
                    ell_level_id: v && 'ell_level_id' in v ? v.ell_level_id : [],
                    meal_status_id: v && 'meal_status_id' in v ? v.meal_status_id : []
                }
                
                // Extract custom demographic filters
                this.customDemographicFilters = {}
                if (v) {
                    Object.keys(v).forEach(key => {
                        if (key.startsWith('demo_') && !defaultDemographicKeys.includes(key)) {
                            this.customDemographicFilters[key] = v[key]
                        }
                    })
                }
            },
            immediate: true
        }
    },

    mounted() {
        this.loadSisData()
    },

    methods: {
        openDialog() {
            this.showDialog = true
        },

        apply() {
            // Merge default demographic filters and custom demographic filters
            const combinedFilters = {
                ...this.defaultDemographicFilters,
                ...this.customDemographicFilters
            }
            this.$emit('apply', combinedFilters)
            this.close()
        },

        close() {
            this.showDialog = false
        },

        clear() {
            this.defaultDemographicFilters = {
                disability_type_id: [],
                ell_flag: null,
                ell_level_id: [],
                meal_status_id: []
            }
            this.customDemographicFilters = {}
        },

        loadSisData() {
            // Load disability types
            this.$modelGet('studentDisabilityType').then(data => {
                this.postReadDemo(data, 'disability_type_id')
            }).catch(error => {
                console.error('Error loading disability types:', error)
            })

            // Load ELP levels
            this.$modelGet('ellLevel').then(data => {
                this.postReadDemo(data, 'ell_level_id')
            }).catch(error => {
                console.error('Error loading ELP levels:', error)
            })

            // Only load meal status if user has permissions
            if (this.$hasSecurity('SEARCH_MEAL_STATUS') || this.$hasSecurity('DRILLDOWN_MEAL_STATUS')) {
                this.$modelGet('mealStatusType').then(data => {
                    this.postReadDemo(data, 'meal_status_id')
                }).catch(error => {
                    console.error('Error loading meal status types:', error)
                })
            }
        },

        postReadDemo(data, model) {
            if (data && Array.isArray(data)) {
                let field = this.defaultDemographics.find(f => f.model === model)
                if (field) {
                    const defaultOptions = field.items.filter(item => item.id === -1 || item.id === 0)
                    
                    // Fallback logic for ELP Level display name coming from multiple sources
                if (model === 'ell_level_id') {
                    const processedData = data.map(item => ({
                        ...item,
                        display_name: item.display_name || item.name
                    }))
                    field.items = [...defaultOptions, ...processedData]
                } else {
                        field.items = [...defaultOptions, ...data]
                    }
                }
            }
        },

        handleSelectionConflicts(field) {
            const data = this.defaultDemographicFilters[field.model]
            
            if (Array.isArray(data) && data.length > 0) {
                const lastSelected = data[data.length - 1]
                const lastSelectedId = lastSelected?.id
                
                // If user selected "Any" (-1) or "None" (0), clear all other selections
                if (lastSelectedId === -1 || lastSelectedId === 0) {
                    this.defaultDemographicFilters[field.model] = [lastSelected]
                } 
                // If user selected individual option, remove "Any" or "None" if present
                else if (lastSelectedId > 0) {
                    this.defaultDemographicFilters[field.model] = data.filter(item => item.id > 0)
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.label {
    margin-top: 14px;
    cursor: pointer;
    color: var(--v-primary-base);
}

.filter-icon {
    color: var(--v-primary-base);
    padding-right: 4px;
}

.filter-content {
    padding: 16px;

    .filter-label {
        width: 44%;
        font-weight: 500;
        align-self: center;
    }

    .filter-component {
        width: 56%;
        margin-left: auto;
    }

    .custom-demographics-section {
        margin-top: 24px;
        
        ::v-deep .advanced-demographic-filter {
            margin-bottom: 12px;
        }
    }
}

</style>
